import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteDialog from 'Components/DeleteDialog'
import type { ErrorFields } from 'Components/FieldErrors/FieldErrors'
import PageTitle from 'Components/PageTitle'
import SaveDialog from 'Components/SaveDialog'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import { CampaignDetails } from 'Services/SalesManagement/Features/Campaign/Components/CampaignDetails'
import { SingleCodeCampaignDetails } from 'Services/SalesManagement/Features/Campaign/Components/SingleCodeCampaignDetails'
import {
  CampaignType,
  createSingleCodeCampaign,
  DiscountType,
  deleteSingleCodeCampaign,
  editSingleCodeCampaign,
  fetchCampaignOptions,
  fetchSingleSingleCodeCampaign,
  type SingleCodeCampaignDetailShape
} from 'Services/SalesManagement/Features/Campaign/campaignSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import DeleteIcon from '@mui/icons-material/DeleteForever'
import { Button, DialogContentText } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function SingleCodeCampaignDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/campaigns#${CampaignType.singleCode}`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()
  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)
  const initialCampaignDetails: SingleCodeCampaignDetailShape = {
    id: null,
    start_date: null,
    end_date: null,
    name: '',
    authorities: [],
    sales_channel_group: null,
    product_codes: [],
    discount_type: DiscountType.percent,
    discount_amount: 0,
    discount_code: ''
  }

  const fieldErrorsDefaultState: CampaignDetailErrors = {
    id: [],
    start_date: [],
    end_date: [],
    name: [],
    authorities: [],
    sales_channel_group: [],
    product_codes: [],
    discount_type: [],
    discount_amount: [],
    discount_code: []
  }

  type CampaignDetailErrors = ErrorFields<SingleCodeCampaignDetailShape>

  const [campaignDetails, setCampaignDetails] =
    useState<SingleCodeCampaignDetailShape>(initialCampaignDetails)
  const [fieldErrors, setFieldErrors] = useState<CampaignDetailErrors>(fieldErrorsDefaultState)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleteActionLoading, setIsDeleteActionLoading] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const navigate = useNavigate()

  const isNewInstance = !instanceIdParam

  const pageTitle = isNewInstance
    ? t`Create new single code campaign`
    : t`Edit single code campaign`

  const { submitLoading, singleCampaignLoading, singleSingleCodeCampaign, optionsLoading } =
    useTypedSelector(state => state.campaign)

  useEffect(() => {
    dispatch(fetchCampaignOptions())
    if (!isNewInstance) {
      dispatch(
        fetchSingleSingleCodeCampaign({
          id: instanceIdParam
        })
      )
        .then(unwrapResult)
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the campaign.`)
        })
    }
  }, [dispatch, isNewInstance, instanceIdParam])

  useEffect(() => {
    if (!isNewInstance && singleSingleCodeCampaign) {
      setCampaignDetails(singleSingleCodeCampaign)
    }
  }, [singleSingleCodeCampaign, isNewInstance])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = useSalesManagementBreadcrumbs([
    {
      title: t`Campaigns`,
      link: baseUrl
    },
    {
      title: pageTitle
    }
  ])

  const setDetails = (
    value: string | number | string[] | Date,
    name: keyof SingleCodeCampaignDetailShape
  ) => {
    setCampaignDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isNewInstance) {
      dispatch(createSingleCodeCampaign(campaignDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(baseUrl)
          SnackbarUtils.success(t`Campaign successfully added.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error creating the campaign.`)
        })
    } else {
      dispatch(editSingleCodeCampaign(campaignDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(baseUrl)
          SnackbarUtils.success(t`Campaign successfully updated.`)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(t`There was an error editing the campaign.`)
        })
    }
  }

  const onDelete = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const handleDelete = () => {
    setIsDeleteActionLoading(true)
    dispatch(
      deleteSingleCodeCampaign({
        id: instanceIdParam
      })
    )
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(t`Campaign successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the campaign.`)
      })
      .finally(() => {
        setIsDeleteActionLoading(false)
        navigate(baseUrl)
      })
  }

  const isLoading = singleCampaignLoading === 'pending' || optionsLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton label={t`Back`} link={previousLocation} />
        <Box sx={{ display: 'flex', maxWidth: 800, justifyContent: 'space-between' }}>
          <PageTitle>{pageTitle}</PageTitle>
          {!isNewInstance && (
            <>
              <Button
                onClick={onDelete}
                startIcon={<DeleteIcon />}
                color="error"
                sx={{
                  display: 'none'
                }} /** Campaign deletion currently not implemented in the backend **/
              >{t`Delete campaign`}</Button>
              <DeleteDialog
                open={isDeleteDialogOpen}
                handleClose={() => setIsDeleteDialogOpen(false)}
                handleSubmit={handleDelete}
                loading={isDeleteActionLoading}
                title={t`Delete campaign`}
              >
                <Typography component="span" color="text.primary">
                  {t`Are you sure you want to delete the campaign?`}
                </Typography>
              </DeleteDialog>
            </>
          )}
        </Box>
      </Box>
      <WithLoader loading={isLoading}>
        <Box>
          <StyledForm onSubmit={handleSave} formRef={formRef}>
            <CampaignDetails
              campaignDetails={campaignDetails}
              fieldErrors={fieldErrors}
              handleSetDetails={setDetails}
            >
              <SingleCodeCampaignDetails
                campaignDetails={campaignDetails}
                handleSetDetails={setDetails}
                fieldErrors={fieldErrors}
              />
            </CampaignDetails>
            <SaveDialog
              open={isSaveDialogOpen}
              handleClose={() => setIsSaveDialogOpen(false)}
              handleSubmit={handleSubmit}
              loading={isSubmitLoading}
              title={t`Save campaign`}
            >
              <DialogContentText>
                <Typography component="span" color="text.primary">
                  {t`Are you sure you want to save the campaign?`}
                </Typography>
              </DialogContentText>
            </SaveDialog>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              onClick={() => setIsSaveDialogOpen(true)}
              size="large"
              iconPosition="end"
            >
              {t`Save campaign`}
            </CustomLoadingButton>
          </StyledForm>
        </Box>
      </WithLoader>
    </Box>
  )
}
